import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  EnumService,
  DocAnexoService,
} from '@app/features-modules/gestao-bolseiros/components/api/services';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { finalize } from 'rxjs/operators';
@Component({
  standalone: true,
  selector: 'app-upload-doc-anexo-modal',
  templateUrl: './upload-doc-anexo-modal.component.html',
  styleUrls: ['./upload-doc-anexo-modal.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectModule,
    Button,
    InputText,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
})
export class UploadDocAnexoModalComponent implements OnInit {
  private ref = inject(DynamicDialogRef);
  private cfg = inject(DynamicDialogConfig);
  private fb = inject(FormBuilder);
  private docSrv = inject(DocAnexoService);
  private enums = inject(EnumService);
  private msg = inject(MessageService);

  form!: FormGroup;
  tipoDocOptions: any[] = [];
  estadoDocOptions: any[] = [];
  private confirm = inject(ConfirmationService);

  entidadeID!: number;
  tipoDocumentoPadrao!: number;

  // Upload
  file?: File | null = null;
  fileError = '';
  dragOver = false;
  uploading = false;

  // Restrições
  accept = '.pdf';
  maxSizeMb = 10;

  readonly ALLOWED_DOCS = [
    'MEMORANDO DE PAGAMENTO DO SUBSIDIO DE APOIO Á FORMAÇÃO',
    'CANCELAMENTO DE BOLSA',
    'MEMORANDO DE APOIO PARA A DEFESA DA MONOGRAFIA',
    'DECLARAÇÃO PARA VISTOS - EMBAIXADAS',
    'FICHA DE CADASTRO',
  ] as const;

  ngOnInit(): void {
    this.entidadeID = this.cfg.data?.entidadeID;
    this.tipoDocumentoPadrao = this.cfg.data?.tipoDocumentoPadrao ?? 5;

    this.form = this.fb.group({
      EntidadeID: [this.entidadeID, Validators.required],
      TipoDocumento: [this.tipoDocumentoPadrao, Validators.required],
      EstadoDocumento: [3, Validators.required], // 3 = Carregado
      Descricao: [''],
      File: [null, Validators.required], // para validar no form
    });

    this.loadEnums();
  }

  ctrl(name: string) {
    return this.form.get(name)!;
  }

  loadEnums() {
    this.enums.getAllTipoDocumentoEnum().subscribe((r) => {
      this.tipoDocOptions = Object.entries(r.data)
        .map(([id, nome]) => ({ id: +id, designacao: String(nome) }))
        .filter((opt) => this.ALLOWED_DOCS.includes(opt.designacao as any))
        .sort(
          (a, b) =>
            this.ALLOWED_DOCS.indexOf(a.designacao as any) -
            this.ALLOWED_DOCS.indexOf(b.designacao as any)
        );
    });
    this.enums.getAllEnumEstadoDocumento().subscribe((r) => {
      this.estadoDocOptions = Object.entries(r?.data ?? {}).map(
        ([id, nome]: any) => ({ id: +id, designacao: nome })
      );
    });
  }

  // Drag & Drop
  onDragOver(ev: DragEvent) {
    ev.preventDefault();
    this.dragOver = true;
  }
  onDragLeave(_: DragEvent) {
    this.dragOver = false;
  }
  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.dragOver = false;
    const f = ev.dataTransfer?.files?.[0];
    if (f) this.handleFile(f);
  }

  onFileChange(ev: any) {
    const f: File | undefined = ev?.target?.files?.[0];
    if (f) this.handleFile(f);
  }

  removeFile() {
    this.file = null;
    this.fileError = '';
    this.ctrl('File').setValue(null);
  }

  private handleFile(f: File) {
    // valida extensão
    const okType = this.accept
      .split(',')
      .some((ext) => f.name.toLowerCase().endsWith(ext.trim()));
    if (!okType) {
      this.fileError = 'Formato inválido. Use PDF/JPG/PNG/DOC/DOCX.';
      this.removeFile();
      return;
    }

    // valida tamanho
    const maxBytes = this.maxSizeMb * 1024 * 1024;
    if (f.size > maxBytes) {
      this.fileError = `O ficheiro excede ${this.maxSizeMb} MB.`;
      this.removeFile();
      return;
    }

    this.fileError = '';
    this.file = f;
    this.ctrl('File').setValue('ok'); // apenas marca como válido
  }

  submit() {
    if (!this.form.valid || !this.file) {
      this.ctrl('TipoDocumento').markAsTouched();
      this.ctrl('EstadoDocumento').markAsTouched();
      this.ctrl('File').markAsTouched();
      this.msg.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha os campos e selecione um ficheiro.',
      });
      return;
    }

    this.confirm.confirm({
      header: 'Substituir documento?',
      icon: 'pi pi-exclamation-triangle',
      message: 'O documento anterior será descontinuado. Deseja continuar?',
      acceptLabel: 'Sim, continuar',
      rejectLabel: 'Cancelar',
      accept: () => this.save(),
    });
  }

  save() {
    const { EntidadeID, TipoDocumento, EstadoDocumento, Descricao } =
      this.form.getRawValue();

    const fd = new FormData();
    fd.append('EntidadeID', String(EntidadeID));
    fd.append('TipoDocumento', String(TipoDocumento));
    fd.append('EstadoDocumento', String(EstadoDocumento));
    if (Descricao) fd.append('Descricao', Descricao);
    fd.append('File', this.file!, this.file!.name);

    this.uploading = true;

    this.docSrv
      .save(fd)
      .pipe(finalize(() => (this.uploading = false)))
      .subscribe({
        next: (res: any) => {
          const codigo = res?.retorno?.codigo ?? 200;
          const ok = codigo === 200 || res?.data === true;

          if (ok) {
            this.ref.close(true); // o pai só mostra sucesso quando vier true
          } else {
            // não fecha com sucesso
            this.ref.close(false);
          }
        },
        error: (err) => {
          // tenta extrair mensagem do backend
          const detalhe =
            err?.error?.retorno?.mensagem ||
            err?.message ||
            'Falha ao carregar.';
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: detalhe,
          });
          this.ref.close(false);
          console.error(err);
        },
      });
  }

  close() {
    this.ref.close(false);
  }
}
